import { useState } from 'react';

const donationTypes = [
  { id: 'monthly', label: '정기 후원', icon: 'ri-refresh-line', desc: '매월 자동 후원' },
  { id: 'once', label: '일시 후원', icon: 'ri-hand-coin-line', desc: '1회 후원' },
  { id: 'goods', label: '물품 후원', icon: 'ri-gift-line', desc: '생필품, 교육 도구 등' },
  { id: 'volunteer', label: '재능 기부', icon: 'ri-user-star-line', desc: '전문 재능 나눔' },
];

const amounts = [10000, 30000, 50000, 100000];

const impacts = [
  { amount: '월 1만원', effect: '활동지원사 1인 심리 상담 1회 지원', icon: 'ri-heart-pulse-line' },
  { amount: '월 3만원', effect: '장애인 가족 1가정 월간 활동지원 보조금', icon: 'ri-home-heart-line' },
  { amount: '월 5만원', effect: '활동지원사 1인 전문 교육 과정 수강', icon: 'ri-graduation-cap-line' },
  { amount: '월 10만원', effect: '중증 장애인 보조기기 구매 및 수리 지원', icon: 'ri-wheelchair-line' },
];

export default function DonationSection() {
  const [donationType, setDonationType] = useState('monthly');
  const [amount, setAmount] = useState(30000);
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('donationType', donationType);
      params.append('amount', customAmount || String(amount));
      params.append('name', formData.name);
      params.append('email', formData.email);
      params.append('phone', formData.phone);
      params.append('message', formData.message);
      await fetch('https://readdy.ai/api/form/d70f4bf9hgr6oot2qu70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="donation">
      <div className="relative w-full min-h-96 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/fac76c5d0dadb170ff965656541d0ba0.jpg"
          alt="후원 배경"
          className="w-full h-full object-cover object-top absolute inset-0"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-800/70 to-stone-700/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-24">
          <div className="max-w-2xl">
            <p className="text-amber-400 text-sm font-semibold tracking-wider mb-4 uppercase">Support Us</p>
            <h2 className="text-6xl font-bold text-white leading-tight mb-6">
              여러분의 작은 관심이<br />
              <span className="text-amber-400">큰 변화를 만듭니다</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              후원금은 활동지원사 교육, 장애인 생활 지원, 복지 프로그램 운영에 투명하게 사용됩니다.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {impacts.map((item) => (
                <div key={item.amount} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-500/30 rounded-xl mb-3">
                    <i className={`${item.icon} text-amber-400 text-xl`}></i>
                  </div>
                  <p className="text-amber-300 font-bold text-sm mb-1">{item.amount}</p>
                  <p className="text-white/70 text-xs leading-relaxed">{item.effect}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-stone-50 py-24">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h3 className="text-3xl font-bold text-stone-800 mb-8">후원 방법 선택</h3>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {donationTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setDonationType(type.id)}
                    className={`p-5 rounded-2xl border-2 text-left transition-all cursor-pointer ${
                      donationType === type.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${
                      donationType === type.id ? 'bg-amber-100' : 'bg-stone-100'
                    }`}>
                      <i className={`${type.icon} text-xl ${donationType === type.id ? 'text-amber-600' : 'text-stone-500'}`}></i>
                    </div>
                    <p className={`font-bold text-sm mb-0.5 ${donationType === type.id ? 'text-amber-700' : 'text-stone-700'}`}>
                      {type.label}
                    </p>
                    <p className="text-stone-500 text-xs">{type.desc}</p>
                  </button>
                ))}
              </div>

              {(donationType === 'monthly' || donationType === 'once') && (
                <div>
                  <p className="text-stone-600 font-semibold mb-4">후원 금액 선택</p>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {amounts.map((a) => (
                      <button
                        key={a}
                        onClick={() => { setAmount(a); setCustomAmount(''); }}
                        className={`py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          amount === a && !customAmount
                            ? 'bg-amber-600 text-white'
                            : 'bg-white border border-stone-200 text-stone-700 hover:border-amber-300'
                        }`}
                      >
                        {(a / 10000).toFixed(0)}만원
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    placeholder="직접 입력 (원)"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-700 text-sm focus:outline-none focus:border-amber-400 bg-white"
                  />
                </div>
              )}

              <div className="mt-8 p-5 bg-white rounded-2xl border border-stone-200">
                <h4 className="font-semibold text-stone-700 mb-3 text-sm">후원금 사용 현황 (2025년 연간)</h4>
                <div className="space-y-3">
                  {[
                    { label: '활동지원사 교육', pct: 40, color: 'bg-amber-500' },
                    { label: '장애인 생활 지원', pct: 35, color: 'bg-rose-400' },
                    { label: '상담 서비스', pct: 15, color: 'bg-green-500' },
                    { label: '운영 및 관리', pct: 10, color: 'bg-stone-400' },
                  ].map((item) => (
                    <div key={item.label}>
                      <div className="flex justify-between mb-1">
                        <span className="text-stone-600 text-xs">{item.label}</span>
                        <span className="text-stone-600 text-xs font-semibold">{item.pct}%</span>
                      </div>
                      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border border-stone-200">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-6">
                    <i className="ri-heart-fill text-amber-600 text-4xl"></i>
                  </div>
                  <h4 className="text-stone-800 font-bold text-2xl mb-2">감사합니다!</h4>
                  <p className="text-stone-500 leading-relaxed mb-8">
                    소중한 후원 신청을 받았습니다.<br />
                    아래 계좌로 입금해 주시면 후원이 완료됩니다.
                  </p>

                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-left">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-600 rounded-lg">
                        <i className="ri-bank-line text-white text-sm"></i>
                      </div>
                      <span className="font-bold text-stone-800">후원 계좌 안내</span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-amber-100">
                        <span className="text-stone-500 text-sm">은행</span>
                        <span className="font-semibold text-stone-800">신한은행</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-amber-100">
                        <span className="text-stone-500 text-sm">계좌번호</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-amber-700 text-lg tracking-wide">100-034-056326</span>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText('100-034-056326')}
                            className="w-7 h-7 flex items-center justify-center bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer"
                            title="복사"
                          >
                            <i className="ri-file-copy-line text-amber-600 text-sm"></i>
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-stone-500 text-sm">예금주</span>
                        <span className="font-semibold text-stone-800">대한효복지센터</span>
                      </div>
                    </div>
                    <p className="text-stone-400 text-xs mt-4 leading-relaxed">
                      입금 후 영수증 발급을 원하시면 <span className="text-amber-600 font-medium">misoo3323@naver.com</span>으로 연락해 주세요.<br />
                      연말정산 기부금 영수증을 발급해 드립니다.
                    </p>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <h4 className="font-bold text-stone-800 text-xl mb-6">후원 신청서</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-stone-500 text-sm mb-1.5 block">이름 *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="홍길동"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-700 text-sm focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <div>
                      <label className="text-stone-500 text-sm mb-1.5 block">연락처 *</label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="010-0000-0000"
                        className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-700 text-sm focus:outline-none focus:border-amber-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-stone-500 text-sm mb-1.5 block">이메일 *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="example@email.com"
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-700 text-sm focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-stone-500 text-sm mb-1.5 block">전달 말씀</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      placeholder="후원하시는 이유나 전달하고 싶은 말씀을 적어주세요. (최대 500자)"
                      rows={3}
                      className="w-full border border-stone-200 rounded-xl px-4 py-3 text-stone-700 text-sm focus:outline-none focus:border-amber-400 resize-none"
                    />
                    <p className="text-stone-400 text-xs mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">후원 유형</span>
                      <span className="font-semibold text-stone-800">
                        {donationTypes.find((d) => d.id === donationType)?.label}
                      </span>
                    </div>
                    {(donationType === 'monthly' || donationType === 'once') && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-stone-600">후원 금액</span>
                        <span className="font-bold text-amber-700">
                          {Number(customAmount || amount).toLocaleString()}원
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-amber-600 hover:bg-amber-700 disabled:bg-stone-300 text-white rounded-xl font-bold text-base transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '처리 중...' : '후원 신청하기'}
                  </button>
                  <p className="text-stone-400 text-xs text-center">
                    후원금은 세금 공제 혜택이 제공됩니다. 연말정산 시 영수증을 발급해 드립니다.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
